/**
 * @file historyLock.ts
 * @brief B39 (28-10-2026): items older than this user's history window.
 *
 * @details
 * The backend hides older meetings, notes and interpretation sessions from
 * its lists and answers 403 `HISTORY_LOCKED` on their detail (Free keeps 30
 * days; the viewer's plan decides). DES 28-10: keep that rule for launch and
 * only fix how it shows. Lists that still reach an old item (to-dos, Ask
 * sources, Inbox) mark it with a lock using `history.from` from
 * GET /billing/subscriptions; the detail shows the server's sentence, never
 * "Couldn't load".
 *
 * Pure (relative imports only) so node:test can load it.
 */

/** True when `err` is the 403 HISTORY_LOCKED refusal (ApiClientError or a body). */
export function isHistoryLockedError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { code?: unknown; status?: unknown };
  return e.code === "HISTORY_LOCKED";
}

/** The server's sentence of a HISTORY_LOCKED error, or null. */
export function historyLockedServerMessage(err: unknown): string | null {
  if (!isHistoryLockedError(err)) return null;
  const m = (err as { message?: unknown }).message;
  return typeof m === "string" && m.trim() ? m.trim() : null;
}

/**
 * True when an item dated `itemDate` is before the window start `from`
 * (same comparison as the backend's isHistoryLocked). No date or no window
 * = not locked.
 */
export function isPastHistory(
  itemDate: string | Date | null | undefined,
  from: string | null | undefined,
): boolean {
  if (!itemDate || !from) return false;
  const t = new Date(itemDate).getTime();
  const f = Date.parse(from);
  return Number.isFinite(t) && Number.isFinite(f) && t < f;
}
